import React from 'react';
import ReactDOM from 'react-dom/client';
import * as SentryReact from '@sentry/react';
import App from './App';
import { initSentry } from './sentry';
import { T } from './theme';
import { installFocusZoomGuard } from './viewportReset';

// Must run before anything else mounts so errors thrown during the very first
// render are still captured.
initSentry();

// Clamp the viewport while an editable field is focused so iOS cannot
// focus-zoom (it does so on iOS 26 even at 16px — see viewportReset.js), and
// release the clamp the moment focus leaves so pinch zoom stays available.
installFocusZoomGuard();

// Shown instead of a white screen when a render throws. `eventId` is present
// only when Sentry actually reported (production); in development the boundary
// still catches, it just has no code to show.
const ErrorFallback = ({ error, resetError, eventId }) => {
  const code = eventId ? eventId.slice(0, 8).toUpperCase() : null;
  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.text,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', textAlign: 'center',
      }}>
        <div style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 26, color: T.text, marginBottom: 10,
        }}>
          Something went wrong
        </div>
        <p style={{ fontSize: 15, lineHeight: 1.55, color: T.textMuted, margin: '0 0 22px' }}>
          Scorify hit an unexpected error and stopped. Your saved data is safe — reopening
          the app will pick things back up.
        </p>

        {code && (
          <div style={{ marginBottom: 22 }}>
            <div style={{
              fontSize: 11, letterSpacing: '0.09em', textTransform: 'uppercase',
              color: T.textMuted, marginBottom: 8,
            }}>
              Error code
            </div>
            <div style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 22, letterSpacing: '0.14em', color: T.accent, fontWeight: 600,
            }}>
              {code}
            </div>
            <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 10, lineHeight: 1.5 }}>
              Send this code to support@scorify.to and we can look up exactly what happened.
            </div>
          </div>
        )}

        <button
          onClick={resetError}
          style={{
            width: '100%', padding: '13px 20px', background: T.accent, color: '#ffffff',
            border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Try again
        </button>
        <button
          onClick={() => { window.location.href = '/'; }}
          style={{
            width: '100%', padding: '13px 20px', marginTop: 10, background: 'transparent',
            color: T.textMuted, border: `1px solid ${T.borderStrong}`, borderRadius: 10,
            fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Back to start
        </button>
      </div>
    </div>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <SentryReact.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback error={error} resetError={resetError} eventId={eventId} />
      )}
    >
      <App />
    </SentryReact.ErrorBoundary>
  </React.StrictMode>
);

// LAUNCH TIMING: first paint = two rAFs after render commits (the frame the
// first content is actually on screen). Logged relative to the earliest webview
// JS (__scorifyBoot) so cold-launch cost is measurable from Xcode's console.
try {
  const boot = window.__scorifyBoot || performance.timeOrigin;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    window.__scorifyFirstPaint = Date.now();
    console.log('[launch] first paint:', Date.now() - boot, 'ms since boot');
  }));
} catch {}
